import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiMapPin, FiUserPlus, FiUserCheck, FiArrowLeft } from 'react-icons/fi';
import Navbar from '../components/Navbar';
import { userAPI, matchAPI } from '../services/api';
import { useAuthContext } from '../context/AuthContext';

const PublicProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuthContext();
  
  const [profileUser, setProfileUser] = useState(null);
  const [userMatches, setUserMatches] = useState([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [followLoading, setFollowLoading] = useState(false);

  useEffect(() => {
    if (currentUser && currentUser._id === id) {
      navigate('/profile');
      return;
    }
    loadData();
  }, [id, currentUser]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [userRes, matchesRes] = await Promise.all([
        userAPI.getUserById(id),
        matchAPI.getUserMatches(id)
      ]);

      if (userRes.success) {
        setProfileUser(userRes.user);
        // Check if current user is following
        if (currentUser) {
          const followingList = currentUser.following || [];
          const currentUserIdStr = currentUser._id?.toString() || currentUser.id?.toString();
          
          // Also check the target user's followers list to be safe
          const followersList = userRes.user.followers || [];
          const isFollower = followersList.some(f => 
            (f._id || f).toString() === currentUserIdStr
          );
          
          setIsFollowing(isFollower);
        }
      }
      
      if (matchesRes.success) {
        setUserMatches(matchesRes.matches);
      }
    } catch (err) {
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleFollowToggle = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    try {
      setFollowLoading(true);
      if (isFollowing) {
        await userAPI.unfollowUser(id);
        setIsFollowing(false);
        setProfileUser(prev => ({
          ...prev,
          followers: prev.followers.filter(f => (f._id || f).toString() !== (currentUser._id || currentUser.id).toString())
        }));
      } else {
        await userAPI.followUser(id);
        setIsFollowing(true);
        setProfileUser(prev => ({
          ...prev,
          followers: [...prev.followers, currentUser._id || currentUser.id]
        }));
      }
    } catch (err) {
      alert('Failed to update follow status');
    } finally {
      setFollowLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-black min-h-screen">
        <Navbar hideAuthButtons={true} />
        <div className="max-w-4xl mx-auto px-4 py-12 text-center text-white">Loading...</div>
      </div>
    );
  }

  if (error || !profileUser) {
    return (
      <div className="bg-black min-h-screen">
        <Navbar hideAuthButtons={true} />
        <div className="max-w-4xl mx-auto px-4 py-12 text-center text-red-500">{error || 'User not found'}</div>
      </div>
    );
  }

  const userInitials = profileUser.fullName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase();

  return (
    <div className="bg-black min-h-screen">
      <Navbar hideAuthButtons={true} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-red-500 hover:text-red-400 mb-8 transition"
        >
          <FiArrowLeft size={20} />
          Back
        </button>

        {/* Profile Header */}
        <div className="bg-gray-900 border-l-4 border-red-500 p-8 rounded-sm mb-8 flex flex-col md:flex-row items-center md:items-start gap-8">
          {/* Profile Picture */}
          <div className="flex-shrink-0">
            <div className="w-32 h-32 rounded-full border-4 border-red-500 bg-gray-800 flex items-center justify-center overflow-hidden">
              {profileUser.profileImage ? (
                <img src={profileUser.profileImage} alt={profileUser.fullName} className="w-full h-full object-cover" />
              ) : (
                <span className="text-5xl font-bold text-red-500">{userInitials}</span>
              )}
            </div>
          </div>

          {/* User Info */}
          <div className="flex-grow text-center md:text-left">
            <h1 className="text-4xl font-bold text-white mb-2">{profileUser.fullName}</h1>
            
            {profileUser.location && (
              <div className="flex items-center justify-center md:justify-start gap-2 text-gray-400 text-sm mb-4">
                <FiMapPin className="text-red-500" />
                {profileUser.location}
              </div>
            )}
            
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 md:gap-6 text-white font-bold mb-6">
              <div className="text-center">
                <div className="text-xl md:text-2xl text-red-500">{profileUser.followers?.length || 0}</div>
                <div className="text-[10px] md:text-xs text-gray-500 uppercase tracking-wider">Followers</div>
              </div>
              <div className="text-center border-x border-gray-800 px-4 md:px-6">
                <div className="text-xl md:text-2xl text-red-500">{profileUser.following?.length || 0}</div>
                <div className="text-[10px] md:text-xs text-gray-500 uppercase tracking-wider">Following</div>
              </div>
              <div className="text-center">
                <div className="text-xl md:text-2xl text-red-500">{userMatches.length}</div>
                <div className="text-[10px] md:text-xs text-gray-500 uppercase tracking-wider">Matches Hosted</div>
              </div>
            </div>
          </div>

          {/* Follow Button */}
          <div className="flex-shrink-0 flex flex-col items-center">
            <button
              onClick={handleFollowToggle}
              disabled={followLoading}
              className={`flex items-center gap-2 px-6 py-3 rounded font-bold text-sm uppercase tracking-wider transition ${
                isFollowing 
                  ? 'bg-gray-800 text-white border-2 border-gray-700 hover:border-red-500' 
                  : 'bg-red-500 text-white hover:bg-red-600'
              }`}
            >
              {followLoading ? '...' : isFollowing ? <><FiUserCheck /> Following</> : <><FiUserPlus /> Follow</>}
            </button>
          </div>
        </div>

        {/* Bio and Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {profileUser.bio && (
            <div className="bg-gray-900 p-6 rounded-sm">
              <h3 className="text-gray-500 text-xs font-bold uppercase tracking-widest mb-4">About</h3>
              <p className="text-gray-300">{profileUser.bio}</p>
            </div>
          )}
          
          <div className="bg-gray-900 p-6 rounded-sm">
            <h3 className="text-gray-500 text-xs font-bold uppercase tracking-widest mb-4">Sports Preferences</h3>
            <div className="space-y-4">
              <div>
                <div className="text-gray-400 text-sm mb-1">Preferred Sport</div>
                <div className="text-white font-bold capitalize">{profileUser.preferredSport || 'Not set'}</div>
              </div>
              <div>
                <div className="text-gray-400 text-sm mb-1">Skill Level</div>
                <div className="text-white font-bold capitalize">{profileUser.skillLevel || 'Not set'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicProfile;
